import { cn } from '@/shared/helpers/classnames'
import { getTypedBlocksByTypes } from '@/shared/helpers/compoundPattern'
import { Grid } from '@/shared/ui/layouts/grid'
import { PageContentContext } from '@/shared/ui/layouts/page'

import { Children, type FC, createContext, useContext } from 'react'

import { Content as RadixTabsContent } from '@radix-ui/react-tabs'

import type {
  TabsColumnProps,
  TabsContentSurfaces,
  TabsContentVariant,
  TabsContentWrapperProps,
} from './types'

// Context to share variant and surfaces with sub-components
type TabsContentContextValue = {
  variant: TabsContentVariant
  surfaces: TabsContentSurfaces
  columnIndex: number
}

const TabsContentContext = createContext<TabsContentContextValue>({
  variant: 'single',
  surfaces: 'muted',
  columnIndex: 0,
})

// Mapping from TabsContent variants to Grid columns
const variantToColumns: Record<TabsContentVariant, '1' | '2' | '3'> = {
  single: '1',
  'two-1/2': '2',
  'two-67-33': '3',
}

// Variant class markers (for tests)
const variantClass: Record<TabsContentVariant, string | null> = {
  single: null,
  'two-1/2': 'tabs-content-two-half',
  'two-67-33': 'tabs-content-two-67-33',
}

// Extract individual surfaces from combination
const getSurfacesArray = (surfaces: TabsContentSurfaces): ('plain' | 'muted')[] => {
  if (surfaces === 'plain' || surfaces === 'muted') {
    return [surfaces]
  }
  const [first, second] = surfaces.split('-') as ('plain' | 'muted')[]
  return [first, second]
}

// Extract surface array from PageContent surfaces
const getPageContentSurfacesArray = (
  surfaces: 'plain' | 'muted' | 'plain-plain' | 'plain-muted' | 'muted-plain' | 'muted-muted',
): ('plain' | 'muted')[] => {
  if (surfaces === 'plain' || surfaces === 'muted') {
    return [surfaces]
  }
  const [first, second] = surfaces.split('-') as ('plain' | 'muted')[]
  return [first, second]
}

/**
 * Column for Tabs.Content layouts.
 *
 * @example
 * <Tabs.Content variant="two-1/2" surfaces="muted-muted" value="tab1">
 *   <Tabs.Column>Left column</Tabs.Column>
 *   <Tabs.Column>Right column</Tabs.Column>
 * </Tabs.Content>
 */
export const TabsColumn: FC<TabsColumnProps> = ({ children }) => {
  const { variant, surfaces, columnIndex } = useContext(TabsContentContext)
  const surfacesArray = getSurfacesArray(surfaces)
  const surface = surfacesArray[columnIndex] || 'muted'

  // For two-67-33, the first column occupies 2 out of 3
  const colSpan = variant === 'two-67-33' && columnIndex === 0 ? '2' : '1'

  return (
    <Grid.Cell colSpan={colSpan}>
      <div
        className={cn('tabs-content-column', `tabs-content-column-${columnIndex}`, {
          'tabs-content-surface-muted': surface === 'muted',
          'tabs-content-surface-plain': surface === 'plain',
        })}
      >
        {children}
      </div>
    </Grid.Cell>
  )
}

TabsColumn.displayName = 'TabsColumn'

/**
 * TabsContentWrapper root component.
 *
 * Layout container for tab content supporting single and two-column variants via the Compound Pattern.
 * Use `Tabs.Column` as children for multi-column layouts.
 *
 * @example
 * // Single column layout
 * <Tabs.Content variant="single" surfaces="muted" value="tab1">
 *   <Tabs.Column>Full-width content</Tabs.Column>
 * </Tabs.Content>
 *
 * @example
 * // Two equal columns with different surfaces
 * <Tabs.Content variant="two-1/2" surfaces="plain-muted" value="tab2">
 *   <Tabs.Column>Left column (plain)</Tabs.Column>
 *   <Tabs.Column>Right column (muted)</Tabs.Column>
 * </Tabs.Content>
 *
 * @example
 * // Asymmetric columns (2:1) with same surface
 * <Tabs.Content variant="two-67-33" surfaces="muted-muted" value="tab3">
 *   <Tabs.Column>Left main content (2/3, muted)</Tabs.Column>
 *   <Tabs.Column>Right sidebar (1/3, muted)</Tabs.Column>
 * </Tabs.Content>
 */
export const TabsContentWrapper: FC<TabsContentWrapperProps> = ({
  value,
  variant = 'single',
  surfaces,
  children,
}) => {
  // Get PageContent context to inherit surface if inside a PageContent
  const pageContentContext = useContext(PageContentContext)

  // If surfaces is not provided and we're inside a PageContent, inherit its surface
  const defaultSurface = (() => {
    if (surfaces !== undefined) return surfaces

    // Check if we're inside a PageContent (variant will be defined)
    if (pageContentContext.variant) {
      const pageContentSurfaces = getPageContentSurfacesArray(pageContentContext.surfaces)
      const inheritedSurface = pageContentSurfaces[pageContentContext.columnIndex] || 'muted'
      return inheritedSurface as TabsContentSurfaces
    }

    return 'muted' as TabsContentSurfaces
  })()

  const finalSurfaces = surfaces ?? defaultSurface
  const numberOfColumns = variantToColumns[variant]
  const blocks = getTypedBlocksByTypes<{ TabsColumn: TabsColumnProps }, ['TabsColumn']>(
    Children.toArray(children),
    ['TabsColumn'],
  )

  const { TabsColumn: columns, ...unmatched } = blocks

  const hasColumns = columns.length > 0

  const normalizedChildren =
    variant === 'single' && !hasColumns ? (
      <TabsColumn>
        <>{Object.values(unmatched)}</>
      </TabsColumn>
    ) : (
      children
    )

  // Inject column index into each child
  const childrenWithContext = Children.toArray(normalizedChildren).map((child, index) => (
    <TabsContentContext.Provider
      key={index}
      value={{ variant, surfaces: finalSurfaces, columnIndex: index }}
    >
      {child}
    </TabsContentContext.Provider>
  ))

  return (
    <RadixTabsContent value={value} className="tabs-content">
      <div
        className={cn('tabs-content-wrapper', variantClass[variant], {
          [`tabs-content-surfaces-${finalSurfaces}`]: true,
        })}
      >
        <Grid columns={numberOfColumns} gap="0">
          {childrenWithContext}
        </Grid>
      </div>
    </RadixTabsContent>
  )
}

TabsContentWrapper.displayName = 'TabsContentWrapper'
